import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area } from 'recharts';
import { TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScoreTimeline() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(90);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/score/history`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || data.count < 2) return null;

  const now = new Date();
  const cutoff = new Date(now.getTime() - range * 24 * 60 * 60 * 1000);
  const filtered = data.history.filter(h => new Date(h.date) >= cutoff);
  if (filtered.length < 2) return null;

  const chartData = filtered.map(h => ({
    date: new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: h.score,
    ai_mentions: h.ai_mentions,
    web_presence: h.web_presence,
    reviews: h.reviews,
    directories: h.directories,
  }));

  const firstScore = filtered[0].score;
  const lastScore = filtered[filtered.length - 1].score;
  const delta = lastScore - firstScore;
  const deltaPct = firstScore > 0 ? Math.round((delta / firstScore) * 100) : 0;

  return (
    <div className="p-5 rounded-sm mb-4" style={{ border: '1px solid #D4C9B8', backgroundColor: '#F7F4EF' }} data-testid="score-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={16} strokeWidth={1.5} style={{ color: '#8C7E6E' }} />
          <h3 className="text-[13px] font-medium" style={{ color: '#3C3226' }}>Score Over Time</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {[30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className="text-[11px] font-medium px-2.5 py-1 transition-colors"
              style={{
                backgroundColor: range === d ? '#2C2520' : 'transparent',
                color: range === d ? '#F0EBE3' : '#8C7E6E',
                borderRadius: '0px',
              }}
              data-testid={`timeline-range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {delta !== 0 && (
        <div className="flex items-center gap-2 mb-3" data-testid="score-delta">
          <div className="flex items-center gap-1 px-2.5 py-1" style={{
            backgroundColor: delta > 0 ? 'rgba(29,107,79,0.06)' : 'rgba(212,68,68,0.06)',
            borderRadius: '2px',
          }}>
            {delta > 0 ? <TrendingUp size={13} style={{ color: '#1D6B4F' }} /> : <TrendingDown size={13} style={{ color: '#D44' }} />}
            <span className="text-[12px] font-semibold" style={{ color: delta > 0 ? '#1D6B4F' : '#D44' }}>
              {delta > 0 ? '+' : ''}{delta} pts
            </span>
          </div>
          {deltaPct !== 0 && (
            <span className="text-[11px]" style={{ color: '#8C7E6E' }}>
              {deltaPct > 0 ? '+' : ''}{deltaPct}% since first scan
            </span>
          )}
        </div>
      )}

      <div style={{ height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1D6B4F" stopOpacity={0.08} />
                <stop offset="100%" stopColor="#1D6B4F" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: '#8C7E6E' }}
              tickLine={false}
              axisLine={{ stroke: '#D4C9B8' }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: '#8C7E6E' }}
              tickLine={false}
              axisLine={false}
              tickCount={3}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="score" fill="url(#scoreGrad)" stroke="none" />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#1D6B4F"
              strokeWidth={2}
              dot={{ r: 3, fill: '#F7F4EF', stroke: '#1D6B4F', strokeWidth: 2 }}
              activeDot={{ r: 5, fill: '#1D6B4F', stroke: '#F7F4EF', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="px-3 py-2" style={{ border: '1px solid #D4C9B8', boxShadow: '0 4px 12px rgba(60,50,38,0.06)', backgroundColor: '#F7F4EF', borderRadius: '2px' }}>
      <p className="text-[11px] font-medium mb-1" style={{ color: '#3C3226' }}>{label}</p>
      <p className="text-[13px] font-semibold" style={{ color: '#1D6B4F' }}>{d.score}/100</p>
      <div className="mt-1 space-y-0.5">
        {d.ai_mentions > 0 && <p className="text-[10px]" style={{ color: '#8C7E6E' }}>AI Mentions: {d.ai_mentions}</p>}
        {d.web_presence > 0 && <p className="text-[10px]" style={{ color: '#8C7E6E' }}>Web: {d.web_presence}</p>}
        {d.reviews > 0 && <p className="text-[10px]" style={{ color: '#8C7E6E' }}>Reviews: {d.reviews}</p>}
        {d.directories > 0 && <p className="text-[10px]" style={{ color: '#8C7E6E' }}>Directories: {d.directories}</p>}
      </div>
    </div>
  );
}
